/**
 * Iter XX.295 — Native app identity for telemetry (commercial P0).
 *
 * Every funnel / diagnostics event on the paid-conversion path must
 * carry the exact binary it fired from:
 *
 *   · build_id     — REACT_APP_BUILD_ID baked into the frontend bundle
 *   · app_version  — native app version  (Capacitor App.getInfo)
 *   · app_build    — native build number (Capacitor App.getInfo)
 *
 * On web, app_version/app_build are null and build_id alone identifies
 * the bundle. The native info is fetched once and cached — callers await
 * at most a short bounded race, never indefinitely.
 *
 * Never throws. Never blocks the caller.
 */
import { isNative } from "./nativePlatform";

let _promise = null;

function _load() {
  if (!isNative()) {
    return Promise.resolve({ app_version: null, app_build: null });
  }
  return (async () => {
    try {
      // Lazy require — same convention as nativePlatform.js: the web
      // bundle never eagerly pulls Capacitor plugin code.
      // eslint-disable-next-line global-require
      const { App } = require("@capacitor/app");
      const info = await App.getInfo();
      return {
        app_version:
          info && info.version != null ? String(info.version) : null,
        app_build:
          info && info.build != null ? String(info.build) : null,
      };
    } catch {
      return { app_version: null, app_build: null };
    }
  })();
}

/**
 * Promise of { app_version, app_build }. Cached after the first call.
 * Never rejects.
 */
export function getNativeAppInfo() {
  if (!_promise) {
    _promise = _load().catch(() => ({ app_version: null, app_build: null }));
  }
  return _promise;
}

/**
 * Best-effort build identity, synchronously. Native version fields are
 * NOT filled here — use getBuildInfo() when they matter.
 */
export function getBuildInfoSync() {
  let build_id = null;
  try {
    build_id = process.env.REACT_APP_BUILD_ID || null;
  } catch {
    /* noop */
  }
  return { build_id, app_version: null, app_build: null };
}

/**
 * Full build identity for telemetry payloads.
 * Awaits at most `timeoutMs` for the native App info, then resolves with
 * nulls for whatever is still missing. Never throws.
 */
export async function getBuildInfo(timeoutMs = 250) {
  const sync = getBuildInfoSync();
  if (!isNative()) return sync;
  try {
    const native = await Promise.race([
      getNativeAppInfo(),
      new Promise((resolve) =>
        setTimeout(
          () => resolve({ app_version: null, app_build: null }),
          timeoutMs
        )
      ),
    ]);
    return { ...sync, ...(native || {}) };
  } catch {
    return sync;
  }
}

/** Test-only: drop the cached promise so tests start clean. */
export function __resetForTests() {
  _promise = null;
}
